/**
 * CodeEditor — thin React wrapper around CodeMirror 6. Manages the
 * EditorView lifecycle (mount, destroy, value sync) and exposes a
 * controlled-input style API (`value` + `onChange`) so callers can
 * treat it like a textarea.
 *
 * Language is selectable: "json" or "python" (extend the LANG_PACKS
 * map below to add more). The styling matches the dashboard's
 * dark/light themes via CSS variables — see `cmTheme` below.
 *
 * Performance note: re-mounting CodeMirror on every render would be
 * expensive (history wipe + full editor rebuild). The editor is
 * created once via useLayoutEffect and `value` is synced via a
 * dispatched transaction only when it diverges from the editor's
 * current document. `onChange` is fired from CodeMirror's
 * updateListener so external state stays in sync.
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { bracketMatching, foldGutter, foldKeymap, indentOnInput, syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { lintKeymap } from "@codemirror/lint";
import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";

const LANG_PACKS = {
    json: json,
    python: python,
};

// Inline theme — uses dashboard CSS vars so dark/light mode flips
// automatically. Kept minimal: monospace + ink/muted ramps already
// established in the rest of the app.
// `height: 100%` makes the editor stretch to its container; the
// autoHeight variant (cmAutoHeightTheme below) overrides this so
// the editor grows with its content instead, suitable for inline
// per-cell editors that shouldn't introduce a second scroll axis.
const cmTheme = EditorView.theme({
    "&": {
        fontSize: "11.5px",
        height: "100%",
        backgroundColor: "var(--plot-bg)",
        color: "var(--ink)",
    },
    ".cm-content": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        padding: "10px 0",
        caretColor: "var(--ink)",
    },
    ".cm-gutters": {
        backgroundColor: "var(--bg)",
        color: "var(--muted-soft)",
        border: "none",
        borderRight: "1px solid var(--line-soft)",
    },
    ".cm-activeLine": { backgroundColor: "rgba(243, 239, 232, 0.03)" },
    ".cm-activeLineGutter": { backgroundColor: "rgba(243, 239, 232, 0.03)" },
    ".cm-line": { padding: "0 10px" },
    "&.cm-focused": { outline: "none" },
    ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
        backgroundColor: "rgba(164, 197, 130, 0.18) !important",
    },
    ".cm-matchingBracket": {
        backgroundColor: "rgba(164, 197, 130, 0.2)",
        outline: "none",
    },
    ".cm-foldPlaceholder": {
        backgroundColor: "transparent",
        border: "1px dashed var(--line)",
        color: "var(--muted)",
        padding: "0 4px",
    },
});

// Overrides for the auto-height variant — content grows the editor
// vertically (no internal vertical scroll; the parent column's scroll
// is the only one in play), but long lines scroll INSIDE the editor.
// overflowX visible would propagate the longest line's width up
// through auto-sized grid/flex tracks and blow the host column out
// (hosts must still bound their track with minmax(0,1fr) / min-w-0
// for the clamp to bite).
const cmAutoHeightTheme = EditorView.theme({
    "&": { height: "auto" },
    ".cm-scroller": { overflowX: "auto", overflowY: "hidden" },
});

export default function CodeEditor({
    value,
    onChange,
    language = "json",
    readOnly = false,
    autoHeight = false,
    className = "",
    minHeight = "180px",
    maxHeight = "60vh",
}) {
    const hostRef = useRef(null);
    const viewRef = useRef(null);
    // Mirror the latest onChange in a ref so the editor's
    // updateListener (created once) always calls the freshest
    // handler without rebuilding the editor.
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    useLayoutEffect(() => {
        if (!hostRef.current) return undefined;
        const langExt = LANG_PACKS[language] ? LANG_PACKS[language]() : LANG_PACKS.json();
        const state = EditorState.create({
            doc: value || "",
            extensions: [
                lineNumbers(),
                highlightActiveLine(),
                highlightActiveLineGutter(),
                history(),
                foldGutter(),
                bracketMatching(),
                closeBrackets(),
                indentOnInput(),
                syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
                langExt,
                keymap.of([
                    ...closeBracketsKeymap,
                    ...defaultKeymap,
                    ...historyKeymap,
                    ...foldKeymap,
                    ...lintKeymap,
                    indentWithTab,
                ]),
                cmTheme,
                ...(autoHeight ? [cmAutoHeightTheme] : []),
                EditorView.editable.of(!readOnly),
                EditorState.readOnly.of(readOnly),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged) {
                        onChangeRef.current?.(update.state.doc.toString());
                    }
                }),
            ],
        });
        const view = new EditorView({ state, parent: hostRef.current });
        viewRef.current = view;
        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // Re-create the editor only when language/readOnly/autoHeight
        // switch — value changes are handled via the sync effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, readOnly, autoHeight]);

    // Sync `value` prop into the editor when it diverges. Guards
    // against feedback loops from the editor's own onChange.
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const current = view.state.doc.toString();
        if (current === (value || "")) return;
        view.dispatch({
            changes: { from: 0, to: current.length, insert: value || "" },
        });
    }, [value]);

    // Auto-height variant lets the host expand with content (no
    // internal scroll), so the parent column's scroll is the only
    // one in play. Fixed-height variant keeps the legacy bounded
    // scroll box.
    const style = autoHeight
        ? undefined
        : { minHeight, maxHeight };
    const wrapperCls = autoHeight
        ? `border border-[color:var(--line)] bg-[color:var(--plot-bg)] ${className}`
        : `overflow-auto border border-[color:var(--line)] bg-[color:var(--plot-bg)] ${className}`;
    return <div ref={hostRef} className={wrapperCls} style={style} />;
}
